import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  IAssetList,
  IExposureMonitoring,
  IScanConfig,
  IScanConfigNetworkAsset,
  IScanConfigNetworkOverview,
  IScanConfigWebAppCreate,
  IScanConfigWebAppOverview,
} from '../../core/store/scan-configuration/models/scan-configuration';

@Injectable({
  providedIn: 'root',
})
export class ScanConfigurationService {
  private baseApiUrl: string = environment.API_BASE_URL;

  constructor(private readonly http: HttpClient) {}

  getScanConfig(): Observable<IScanConfig> {
    return this.http.get<IScanConfig>(`${this.baseApiUrl}/scan-configuration`);
  }

  getWebApp(id: string): Observable<IScanConfigWebAppOverview> {
    return this.http.get<IScanConfigWebAppOverview>(
      `${this.baseApiUrl}/scan-configuration/web-app/${id}`,
    );
  }

  createWebApp(
    data: IScanConfigWebAppCreate,
  ): Observable<IScanConfigWebAppCreate> {
    return this.http.post<IScanConfigWebAppCreate>(
      `${this.baseApiUrl}/scan-configuration/web-app/add`,
      data,
    );
  }

  getNetwork(): Observable<IScanConfigNetworkOverview[]> {
    return this.http.get<IScanConfigNetworkOverview[]>(
      `${this.baseApiUrl}/scan-configuration/network`,
    );
  }

  getNetworkAssets(): Observable<IScanConfigNetworkAsset[]> {
    return this.http.get<IScanConfigNetworkAsset[]>(
      `${this.baseApiUrl}/scan-configuration/network/assets`,
    );
  }

  createNetwork(assetIds: string[]): Observable<IScanConfigWebAppCreate> {
    return this.http.post<IScanConfigWebAppCreate>(
      `${this.baseApiUrl}/scan-configuration/network/add`,
      { assetIds },
    );
  }

  updateNetwork(
    scanConfigId: string,
    assetIds: string[],
  ): Observable<IScanConfigWebAppCreate> {
    return this.http.put<IScanConfigWebAppCreate>(
      `${this.baseApiUrl}/scan-configuration/network/edit`,
      { scanConfigId, enabledAssetIds: assetIds },
    );
  }

  editNetworkAssets(
    hosts: { ipAddress: string; enabled: boolean }[],
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseApiUrl}/scan-configuration/network/edit`,
      { hosts },
    );
  }

  getExposureMonitoring(): Observable<IExposureMonitoring> {
    return this.http.get<IExposureMonitoring>(
      `${this.baseApiUrl}/scan-configuration/exposure-monitoring`,
    );
  }

  // One direction per call: the API refuses a monitor request that would exceed the plan
  // limit, so callers unmonitor first to free headroom, then monitor.
  setExposureMonitoring(
    assetIds: string[],
    monitored: boolean,
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseApiUrl}/scan-configuration/exposure-monitoring`,
      { assetIds, monitored },
    );
  }

  removeConfig(id: string): Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.baseApiUrl}/scan-configuration/remove/${id}`,
    );
  }

  getAssetList(): Observable<IAssetList[]> {
    return this.http.get<IAssetList[]>(
      `${this.baseApiUrl}/scan-configuration/assets`,
    );
  }
}
